import React, { useEffect, useMemo, useState } from "react";
import { Brain, Plus, Save, Trash2, RefreshCw, Search } from "lucide-react";

const EMPTY_FORM = {
    id: null,
    titolo: "",
    ambito: "generale",
    tipo: "direttiva",
    priorita: 100,
    attiva: 1,
    contenuto: "",
};

export default function NozioniNormaAIAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [msg, setMsg] = useState("");

    async function loadData() {
        setLoading(true);
        setMsg("");
        try {
            const r = await fetch("/api/admin/ai_nozioni_list.php", {
                credentials: "include",
            });
            const j = await r.json();
            if (!r.ok || !j?.ok) {
                throw new Error(j?.error || "Errore caricamento nozioni");
            }
            setItems(Array.isArray(j.items) ? j.items : []);
        } catch (e) {
            setMsg(e.message || "Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((x) =>
            [
                x.titolo || "",
                x.ambito || "",
                x.tipo || "",
                x.contenuto || "",
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [items, query]);

    function newItem() {
        setForm({ ...EMPTY_FORM });
        setMsg("");
    }

    function editItem(item) {
        setForm({
            id: item.id ?? null,
            titolo: item.titolo ?? "",
            ambito: item.ambito ?? "generale",
            tipo: item.tipo ?? "direttiva",
            priorita: Number(item.priorita ?? 100),
            attiva: Number(item.attiva ?? 1),
            contenuto: item.contenuto ?? "",
        });
        setMsg("");
    }

    async function saveItem() {
        setSaving(true);
        setMsg("");
        try {
            const r = await fetch("/api/admin/ai_nozioni_save.php", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(form),
            });

            const j = await r.json();
            if (!r.ok || !j?.ok) {
                throw new Error(j?.error || "Errore salvataggio");
            }

            setMsg("Salvato correttamente");
            await loadData();

            if (j.item) {
                editItem(j.item);
            } else {
                setForm({ ...EMPTY_FORM });
            }
        } catch (e) {
            setMsg(e.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    async function deleteItem(id) {
        if (!id) return;
        const ok = window.confirm("Vuoi eliminare questa nozione?");
        if (!ok) return;

        setSaving(true);
        setMsg("");
        try {
            const r = await fetch("/api/admin/ai_nozioni_delete.php", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ id }),
            });

            const j = await r.json();
            if (!r.ok || !j?.ok) {
                throw new Error(j?.error || "Errore eliminazione");
            }

            setMsg("Nozione eliminata");
            setForm({ ...EMPTY_FORM });
            await loadData();
        } catch (e) {
            setMsg(e.message || "Errore eliminazione");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
                            <Brain size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">
                                Nozioni salvate
                            </h2>
                            <p className="text-sm text-slate-500">
                                Direttive e prompt interni per Norma AI
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={newItem}
                        className="px-3 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-2"
                    >
                        <Plus size={16} />
                        Nuova
                    </button>
                </div>

                <div className="p-4 border-b border-slate-100">
                    <div className="relative">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Cerca titolo, ambito, contenuto..."
                            className="w-full rounded-xl border border-slate-200 pl-10 pr-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                    </div>
                </div>

                <div className="max-h-[620px] overflow-auto">
                    {loading ? (
                        <div className="p-4 text-slate-500">Caricamento...</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-4 text-slate-500">Nessuna nozione trovata.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filtered.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => editItem(item)}
                                    className={`w-full text-left p-4 hover:bg-slate-50 transition ${Number(form.id) === Number(item.id) ? "bg-indigo-50/60" : ""
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="font-semibold text-slate-900">
                                                {item.titolo || "Senza titolo"}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-2 text-xs">
                                                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                                                    {item.tipo || "—"}
                                                </span>
                                                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                                                    {item.ambito || "generale"}
                                                </span>
                                                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                                                    priorità {item.priorita ?? 100}
                                                </span>
                                                <span
                                                    className={`px-2 py-1 rounded-full ${Number(item.attiva) === 1
                                                        ? "bg-emerald-100 text-emerald-700"
                                                        : "bg-rose-100 text-rose-700"
                                                        }`}
                                                >
                                                    {Number(item.attiva) === 1 ? "attiva" : "disattiva"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2 text-sm text-slate-600 line-clamp-3 whitespace-pre-wrap">
                                        {item.contenuto}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">
                            {form.id ? "Modifica nozione" : "Nuova nozione"}
                        </h2>
                        <p className="text-sm text-slate-500">
                            Questi contenuti verranno letti da Norma AI prima di rispondere.
                        </p>
                    </div>

                    <button
                        onClick={loadData}
                        className="px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 flex items-center gap-2"
                    >
                        <RefreshCw size={16} />
                        Aggiorna
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {msg ? (
                        <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">
                            {msg}
                        </div>
                    ) : null}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Titolo
                            </label>
                            <input
                                value={form.titolo}
                                onChange={(e) => setForm((s) => ({ ...s, titolo: e.target.value }))}
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                                placeholder="Es. Stile risposte HACCP"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Ambito
                            </label>
                            <select
                                value={form.ambito}
                                onChange={(e) => setForm((s) => ({ ...s, ambito: e.target.value }))}
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                            >
                                <option value="generale">generale</option>
                                <option value="manuale_haccp">manuale_haccp</option>
                                <option value="audit">audit</option>
                                <option value="ricevimento_merci">ricevimento_merci</option>
                                <option value="etichette">etichette</option>
                                <option value="infestanti">infestanti</option>
                                <option value="sanificazione">sanificazione</option>
                                <option value="temperature">temperature</option>
                                <option value="panic_mode">panic_mode</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Tipo
                            </label>
                            <select
                                value={form.tipo}
                                onChange={(e) => setForm((s) => ({ ...s, tipo: e.target.value }))}
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                            >
                                <option value="direttiva">direttiva</option>
                                <option value="nozione">nozione</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Priorità
                            </label>
                            <input
                                type="number"
                                value={form.priorita}
                                onChange={(e) =>
                                    setForm((s) => ({
                                        ...s,
                                        priorita: Number(e.target.value || 100),
                                    }))
                                }
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                            />
                        </div>
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={Number(form.attiva) === 1}
                            onChange={(e) =>
                                setForm((s) => ({ ...s, attiva: e.target.checked ? 1 : 0 }))
                            }
                        />
                        Nozione attiva
                    </label>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Contenuto
                        </label>
                        <textarea
                            value={form.contenuto}
                            onChange={(e) =>
                                setForm((s) => ({ ...s, contenuto: e.target.value }))
                            }
                            rows={16}
                            className="w-full rounded-2xl border border-slate-200 px-3 py-3 outline-none focus:ring-2 focus:ring-indigo-200"
                            placeholder="Inserisci qui le direttive o le nozioni che Norma AI dovrà leggere prima di rispondere..."
                        />
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                        <button
                            onClick={saveItem}
                            disabled={saving}
                            className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center gap-2"
                        >
                            <Save size={16} />
                            {saving ? "Salvataggio..." : "Salva"}
                        </button>

                        {!!form.id && (
                            <button
                                onClick={() => deleteItem(form.id)}
                                disabled={saving}
                                className="px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 flex items-center gap-2"
                            >
                                <Trash2 size={16} />
                                Elimina
                            </button>
                        )}

                        <button
                            onClick={newItem}
                            className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50"
                        >
                            Nuova scheda
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}